import { useMemo, useRef, useState } from 'react';

import { Building2, Camera, Eye, Globe, Mail, MapPin, ScrollText } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { CompanyRead, CompanyUpdate } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { BrandLockup } from '@/components/brand/BrandLockup';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import { ConfirmModal } from '@/components/ui/Modal';
import { ErrorState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select, Textarea } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCurrentUser } from '@/features/auth/api';
import { CURRENCIES, DATE_FORMATS, asDateFormat, currencyLabel, formatDate } from '@/lib/dates';
import { can } from '@/lib/permissions';
import { localZone, timeZones, zoneOffset } from '@/lib/timezones';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';

import {
  useBranding,
  useCompany,
  useRemoveLogo,
  useUpdateCompany,
  useUploadLogo,
} from '../api';
import styles from './CompanyPage.module.css';

/** The fields this screen edits. Everything else on the record is read-only here. */
const EDITABLE = [
  'name',
  'legal_name',
  'tagline',
  'about',
  'registration_number',
  'licence_number',
  'industry',
  'website',
  'public_email',
  'phone',
  'registered_address',
  'operations_address',
  'timezone',
  'date_format',
  'currency',
] as const;

type EditableField = (typeof EDITABLE)[number];
type Draft = Record<EditableField, string>;

function toDraft(company: CompanyRead): Draft {
  // Built by assignment rather than a mapped spread so the result keeps its exact
  // key type instead of widening to Record<string, string>.
  const draft = {} as Draft;
  for (const field of EDITABLE) draft[field] = company[field];
  return draft;
}

/** Only what actually changed, so an untouched field is never sent back. */
function changedFields(draft: Draft, original: Draft): CompanyUpdate {
  const changes: CompanyUpdate = {};
  for (const field of EDITABLE) {
    if (draft[field] !== original[field]) changes[field] = draft[field];
  }
  return changes;
}

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function CompanyPage() {
  const toast = useToast();
  const { data: currentUser } = useCurrentUser();
  const canEdit = can(currentUser, 'settings', 'edit');

  const company = useCompany();
  const branding = useBranding();
  const updateCompany = useUpdateCompany();
  const uploadLogo = useUploadLogo();
  const removeLogo = useRemoveLogo();

  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmRemoveLogo, setConfirmRemoveLogo] = useState(false);

  const loaded = company.data;
  const original = useMemo(() => (loaded ? toDraft(loaded) : null), [loaded]);

  /*
   * The draft is cleared by saving and by discarding, and by nothing else.
   *
   * It is tempting to drop it whenever the server data changes. That would throw away
   * what somebody is typing every time a background refetch lands, or the moment they
   * upload a logo mid-edit: the upload writes a new profile into the cache, and the
   * half-filled form would empty itself. `original` is recomputed either way, so an
   * outside change still shows up correctly in what counts as dirty.
   */
  const values = draft ?? original;
  const dirty =
    draft !== null && original !== null && EDITABLE.some((f) => draft[f] !== original[f]);

  const unsaved = useUnsavedChanges(dirty);

  const set = (field: EditableField, value: string) => {
    setDraft((current) => ({ ...(current ?? original ?? ({} as Draft)), [field]: value }));
  };

  const discard = () => {
    setDraft(null);
  };

  const save = () => {
    if (!values || !original) return;
    updateCompany.mutate(changedFields(values, original), {
      onSuccess: () => {
        setDraft(null);
        toast.success('Company profile saved.');
      },
      onError: (error) => {
        toast.error(error instanceof ApiError ? error.message : 'That did not save.');
      },
    });
  };

  const pickLogo = (file: File | undefined) => {
    if (!file) return;

    // Checked here as well as on the server. The server is the one that matters; this
    // is only so a 3 MB photograph fails instantly instead of after the upload.
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      toast.error('Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('The logo must be under 2 MB.');
      return;
    }

    uploadLogo.mutate(file, {
      onSuccess: () => {
        toast.success('Logo updated.');
      },
      onError: (error) => {
        toast.error(error instanceof ApiError ? error.message : 'That did not upload.');
      },
    });
  };

  /**
   * The zone list, with whatever is stored guaranteed to be in it.
   *
   * Browsers disagree about zone names. This one reports the viewer as
   * "Asia/Calcutta" and never offers "Asia/Kolkata", and elsewhere it is the other way
   * round. A value saved on one machine and opened on another would otherwise select
   * nothing, and the field would look empty for a profile that is perfectly valid.
   */
  const zoneOptions: ComboboxOption[] = useMemo(() => {
    const stored = values?.timezone;
    const zones = timeZones();
    const all = stored && !zones.includes(stored) ? [stored, ...zones] : zones;

    return all.map((zone) => ({
      value: zone,
      label: zone.replace(/_/g, ' '),
      meta: zoneOffset(zone),
    }));
  }, [values?.timezone]);

  if (company.isPending) {
    return (
      <Page className={styles.page}>
        <PageHeader title="Company profile" />
        <div className={styles.layout}>
          <Panel>
            <Skeleton height={280} />
          </Panel>
          <Panel>
            <Skeleton height={180} />
          </Panel>
        </div>
      </Page>
    );
  }

  if (company.isError || !values || !loaded) {
    return (
      <Page className={styles.page}>
        <PageHeader title="Company profile" />
        <Panel>
          <ErrorState
            message={company.error instanceof ApiError ? company.error.message : undefined}
            onRetry={() => {
              void company.refetch();
            }}
          />
        </Panel>
      </Page>
    );
  }

  // Whether a logo exists, from the profile, not from whether its image loaded.
  // A row pointing at a missing file still has something to remove.
  const hasLogo = branding.data?.has_logo === true;
  const dateFormat = asDateFormat(values.date_format);
  const busy = uploadLogo.isPending || removeLogo.isPending;

  return (
    <Page className={styles.page}>
      <PageHeader
        title="Company profile"
        description="Organisation details used across the portal: on documents, candidate emails and the sign-in screen."
        actions={
          canEdit ? (
            <>
              {dirty ? (
                <Button variant="ghost" onClick={discard}>
                  Discard
                </Button>
              ) : null}
              <Button loading={updateCompany.isPending} disabled={!dirty} onClick={save}>
                Save changes
              </Button>
            </>
          ) : null
        }
      />

      {!canEdit ? (
        <Alert tone="info">
          You can read the company profile but not change it. That needs the settings
          permission, which an administrator can grant.
        </Alert>
      ) : null}

      <div className={styles.layout}>
        {/* ------------------------------------------------------- left -- */}
        <div className={styles.column}>
          <Panel title="Identity" icon={<Building2 size={15} />}>
            <div className={styles.logoRow}>
              <div className={styles.logoBox}>
                <BrandLockup height={48} tone="light" className={styles.logoImage} />
              </div>

              <div className={styles.logoActions}>
                <div className={styles.logoButtons}>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canEdit || busy}
                    loading={uploadLogo.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    <Camera size={14} />
                    {hasLogo ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  {hasLogo ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canEdit || busy}
                      onClick={() => {
                        setConfirmRemoveLogo(true);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
                {/*
                  The prototype offered SVG. It is not accepted: an SVG is a document
                  that can carry script, and this one is served from our own origin to
                  anyone who opens the sign-in page.
                */}
                <p className={styles.logoHint}>PNG, JPEG or WebP, up to 2 MB.</p>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(',')}
                className={styles.fileInput}
                onChange={(event) => {
                  pickLogo(event.target.files?.[0]);
                  // Cleared so choosing the same file twice still fires a change.
                  event.target.value = '';
                }}
              />
            </div>

            <div className={styles.pair}>
              <Field label="Display name">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    value={values.name}
                    disabled={!canEdit}
                    onChange={(event) => {
                      set('name', event.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label="Legal entity name">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    value={values.legal_name}
                    disabled={!canEdit}
                    onChange={(event) => {
                      set('legal_name', event.target.value);
                    }}
                  />
                )}
              </Field>
            </div>

            <Field label="Tagline">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  value={values.tagline}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('tagline', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="About">
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  describedBy={describedBy}
                  value={values.about}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('about', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>

          <Panel title="Registration" icon={<ScrollText size={15} />}>
            <div className={styles.pair}>
              <Field label="Company registration no.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    mono
                    value={values.registration_number}
                    disabled={!canEdit}
                    onChange={(event) => {
                      set('registration_number', event.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label="Recruitment licence no.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    mono
                    value={values.licence_number}
                    disabled={!canEdit}
                    onChange={(event) => {
                      set('licence_number', event.target.value);
                    }}
                  />
                )}
              </Field>
            </div>

            <Field label="Industry">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  value={values.industry}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('industry', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>

          <Panel title="Addresses" icon={<MapPin size={15} />}>
            <Field label="Registered office">
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  describedBy={describedBy}
                  className={styles.address}
                  value={values.registered_address}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('registered_address', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Operations office">
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  describedBy={describedBy}
                  className={styles.address}
                  value={values.operations_address}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('operations_address', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>
        </div>

        {/* ------------------------------------------------------ right -- */}
        <div className={styles.column}>
          <Panel title="Contact" icon={<Mail size={15} />}>
            <Field label="Public email">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  type="email"
                  value={values.public_email}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('public_email', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Phone">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  value={values.phone}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('phone', event.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Website">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  value={values.website}
                  disabled={!canEdit}
                  onChange={(event) => {
                    set('website', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>

          <Panel title="Regional settings" icon={<Globe size={15} />}>
            <Field
              label="Time zone"
              hint={
                // Said outright, because a time zone field usually means "show me
                // everything in this zone" and here it does not.
                <>
                  The organisation&rsquo;s own zone, for documents and records. Times on screen
                  always follow the clock of whoever is looking.{' '}
                  {canEdit && values.timezone !== localZone() ? (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        set('timezone', localZone());
                      }}
                    >
                      Use mine ({localZone()})
                    </button>
                  ) : null}
                </>
              }
            >
              {({ id, describedBy }) => (
                <Combobox
                  id={id}
                  describedBy={describedBy}
                  value={values.timezone}
                  disabled={!canEdit}
                  options={zoneOptions}
                  placeholder="Search time zones"
                  onChange={(zone) => {
                    set('timezone', zone);
                  }}
                />
              )}
            </Field>

            <Field
              label="Date format"
              hint={`Dates read as ${formatDate(new Date(), dateFormat)}.`}
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  value={values.date_format}
                  disabled={!canEdit}
                  options={DATE_FORMATS.map((entry) => ({
                    value: entry.value,
                    label: `${entry.label} (${entry.example})`,
                  }))}
                  onChange={(event) => {
                    set('date_format', event.target.value);
                  }}
                />
              )}
            </Field>

            <Field label="Default currency">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  describedBy={describedBy}
                  value={values.currency}
                  disabled={!canEdit}
                  options={CURRENCIES.map((code) => ({
                    value: code,
                    label: currencyLabel(code),
                  }))}
                  onChange={(event) => {
                    set('currency', event.target.value);
                  }}
                />
              )}
            </Field>
          </Panel>

          <Panel title="How it appears" icon={<Eye size={15} />}>
            {/*
              The sidebar treatment, on the sidebar's own background. A mark that
              reads on white and vanishes on navy is the thing this panel is for.
            */}
            <div className={styles.preview}>
              {/* Exactly what the sidebar renders: the logo and nothing else. The
                  name and tagline used to be set beside it here, but the sidebar no
                  longer writes them, and a preview that shows something the real
                  thing does not is worse than no preview. */}
              <BrandLockup height={40} />
            </div>
            <p className={styles.updated}>
              Last updated {formatDate(loaded.updated_at, dateFormat)}
            </p>
          </Panel>
        </div>
      </div>

      <ConfirmModal
        open={confirmRemoveLogo}
        title="Remove the logo?"
        body="The portal and the sign-in screen go back to the default mark. You can upload a new one at any time."
        confirmLabel="Remove logo"
        danger
        busy={removeLogo.isPending}
        onCancel={() => {
          setConfirmRemoveLogo(false);
        }}
        onConfirm={() => {
          removeLogo.mutate(undefined, {
            onSuccess: () => {
              setConfirmRemoveLogo(false);
              toast.success('Logo removed.');
            },
            onError: (error) => {
              setConfirmRemoveLogo(false);
              toast.error(error instanceof ApiError ? error.message : 'That did not work.');
            },
          });
        }}
      />

      <ConfirmModal
        open={unsaved.blocked}
        title="Discard your changes?"
        body="The company profile has unsaved changes. Leaving now loses them."
        confirmLabel="Discard and leave"
        danger
        onCancel={unsaved.stay}
        onConfirm={() => {
          setDraft(null);
          unsaved.discard();
        }}
      />
    </Page>
  );
}
